import { toast } from "sonner";
import React from 'react'; // React is necessary for JSX in the custom toast

/**
 * @typedef {Object} ButtonText
 * @property {string} [accept] - Custom text for the accept button (when showAcceptDecline is true).
 * @property {string} [decline] - Custom text for the decline button (when showAcceptDecline is true).
 */

/**
 * @typedef {Object} CustomModalProps
 * @property {string} title - The main heading displayed at the top of the modal.
 * @property {ButtonText} [buttontxt] - Optional custom text for the action buttons.
 * @property {string} message - The main body text/content of the modal.
 * @property {() => void} [onAccept] - Optional function to execute when the 'Accept' button is clicked.
 * @property {() => void} [onDecline] - Optional function to execute when the 'Decline' button is clicked.
 * @property {boolean} [showAcceptDecline=false] - If true, displays 'Accept' and 'Decline' buttons; otherwise, shows a single 'Close' button or loading state.
 * @property {boolean} [loading=false] - If true, displays a loading spinner and text, overriding the buttons.
 * @property {string} [loadingText="Processing..."] - Custom text to display during the loading state.
 */

/**
 * Displays a highly customizable modal/toast notification using the 'sonner' library.
 * It supports standard info/close modals, confirmation (accept/decline) modals, and loading states.
 *
 * @param {CustomModalProps} props - The configuration options for the modal.
 * @returns {string | number} The ID of the created toast notification.
 */
const showCustomModal = ({
  title,
  buttontxt,
  message,
  onAccept,
  onDecline,
  showAcceptDecline = false,
  loading = false,
  loadingText = "Processing..."
}) => {
  return toast.custom((t) => (
    <div className="
            bg-[var(--color-surface-input)] 
            text-white 
            font-[Inter] 
            rounded-xl 
            shadow-lg 
            p-6
            w-full
            max-w-sm    sm:max-w-sm    md:max-w-md    lg:max-w-lg 
            min-w-xs
            mr-7 sm:mr-6 md:mr-8
        ">

      <h2 className="text-xl font-bold mb-2 text-[var(--primary-text-color)] ">{title}</h2>
      <p className="text-sm text-[var(--secondary-text-color)] mb-4">{message}</p>

      {showAcceptDecline ? (
        <div className="flex flex-col sm:flex-row justify-end sm:space-x-4 space-y-2 sm:space-y-0">
          <button
            onClick={() => {
              onAccept?.();
              toast.dismiss(t.id);
            }}
            className="px-4 py-2 bg-[var(--color-primary)] text-[var(--primary-text-color)] rounded-md hover:opacity-80 transition-opacity duration-200  cursor-pointer"
          >
            {buttontxt?.accept || "Accept"}
          </button>
          <button
            onClick={() => {
              onDecline?.();
              toast.dismiss(t.id);
            }}
            className="px-4 py-2 bg-[var(--color-text-secondary)] text-[var(--primary-text-color)]  rounded-md hover:opacity-80 transition-opacity duration-200  cursor-pointer"
          >
            {buttontxt?.decline || "Decline"}
          </button>
        </div>
      ) : loading ? (
        <div className="flex flex-col items-center justify-center py-2 space-y-2">
          <div className="relative flex items-center justify-center">
            {/* Loading Spinner */}
            <div className="w-8 h-8 sm:w-10 sm:h-10 border-4 sm:border-4 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin"></div>
          </div>
          {/* Loading Text */}
          <p className="text-sm text-[var(--color-text-secondary)] animate-pulse mt-2 text-center">{loadingText}</p>
        </div>
      ) : (
        <div className="flex justify-end">
          <button
            onClick={() => toast.dismiss(t.id)}
            className="px-4 py-2 bg-[var(--color-primary)] text-[var(--primary-text-color)] rounded-md hover:opacity-80 transition-opacity duration-200 cursor-pointer"
          >
            Close
          </button>
        </div>
      )}
    </div>
  ), {
    position: "bottom-right",
    // Set duration to Infinity for confirmation/loading states so it stays until manually dismissed
    duration: !loading && !showAcceptDecline ? 1500 : Infinity,
  });
};

export default showCustomModal;